import { type ClipboardEvent, type FormEvent, useEffect, useId, useRef, useState } from "react";
import { api } from "./api.ts";
import { Modal } from "./Dialog.tsx";
import { diagnostics, type FeedbackAsk, type FeedbackKind, onOpenFeedback, openFeedback } from "./feedback.ts";
import { heicToJpeg, isHeic } from "./heic.ts";
import { useGrow } from "./hooks.ts";
import { device } from "./opening.tsx";
import { Button, Icon, SegmentedControl } from "./souvenir.ts";

// Send feedback and Report this (features/feedback.md; DESIGN.md, "Feedback, from anywhere"): one form, over whatever
// the page shows. Bug, Idea or Other (Bug first from an error); what happened, in their words; a screenshot if they
// like; and what helps us find it, on unless they turn it off, saying what that is (from a page that broke, the crash
// too). Sent, it thanks them at once.

const MAX_CHARS = 5000;
const MAX_SHOT_BYTES = 10 * 1024 * 1024;

const KINDS: { value: FeedbackKind; label: string }[] = [
  { value: "bug", label: "Bug" },
  { value: "idea", label: "Idea" },
  { value: "other", label: "Other" },
];

const ASKED: Record<FeedbackKind, { label: string; placeholder: string }> = {
  bug: { label: "What happened?", placeholder: "What you did, what you saw, and what you expected" },
  idea: { label: "What would you like Tripmo to do?", placeholder: "What it would help you with" },
  other: { label: "What's on your mind?", placeholder: "Tell us anything" },
};

// The form, opened from anywhere (feedback.ts, openFeedback): kept outside the app's own drawing (main.tsx), so a
// page that broke can still open it.
export function FeedbackHost() {
  const [open, setOpen] = useState<{ ask: FeedbackAsk; n: number } | null>(null);
  useEffect(() => onOpenFeedback((ask) => setOpen((was) => ({ ask, n: (was?.n ?? 0) + 1 }))), []);
  if (!open) return null;
  return <FeedbackDialog key={open.n} ask={open.ask} onClose={() => setOpen(null)} />;
}

// Send feedback, as a button (the profile's; the trip menu has its own item).
export function SendFeedback() {
  return (
    <Button variant="secondary" size="sm" className="tm-edge-start" onClick={() => openFeedback({ kind: "bug" })}>
      Send feedback
    </Button>
  );
}

const size = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

function FeedbackDialog({ ask, onClose }: { ask: FeedbackAsk; onClose: () => void }) {
  const [kind, setKind] = useState<FeedbackKind>(ask.kind);
  const [text, setText] = useState("");
  const growText = useGrow(text);
  const [shot, setShot] = useState<{ file: File; url: string } | null>(null);
  // Whether this browser draws it (it may not draw a HEIC it couldn't turn into a JPEG).
  const [drawn, setDrawn] = useState(true);
  const [include, setInclude] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const textId = useId();
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => () => (shot ? URL.revokeObjectURL(shot.url) : undefined), [shot]);

  const take = async (picked: File | null | undefined) => {
    if (!picked) return;
    setError(null);
    if (!picked.type.startsWith("image/") && !isHeic(picked)) {
      setError(`${picked.name || "That file"} isn't an image. A screenshot is a PNG, JPEG, WebP or HEIC.`);
      return;
    }
    // Drawn where any browser can show it: a HEIC as a JPEG, where this browser reads HEIC.
    const file = isHeic(picked) ? ((await heicToJpeg(picked)) ?? picked) : picked;
    if (file.size > MAX_SHOT_BYTES) {
      setError("That screenshot is over 10 MB. Try a smaller one.");
      return;
    }
    setDrawn(true);
    setShot({ file, url: URL.createObjectURL(file) });
  };

  // A screenshot pasted into the form is taken as if it were chosen.
  const onPaste = (e: ClipboardEvent) => {
    const image = [...e.clipboardData.files].find((f) => f.type.startsWith("image/"));
    if (!image) return;
    e.preventDefault();
    void take(image);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const words = text.trim();
    if (!words) {
      setError(kind === "bug" ? "Say what happened, in a few words." : "Say it in a few words.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.set("kind", kind);
      form.set("text", words);
      if (include) form.set("diagnostics", JSON.stringify(diagnostics(ask)));
      if (shot) form.set("screenshot", shot.file, shot.file.name || "screenshot");
      setSent((await api.sendFeedback(form)).message);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't send it. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const left = MAX_CHARS - text.length;
  const asked = ASKED[kind];
  return (
    <Modal
      title={sent ? "Feedback sent" : "Send feedback"}
      busy={busy}
      focus={sent ? ".tm-feedback-done" : `[id="${textId}"]`}
      onCancel={onClose}
      className="tm-feedback-dialog"
    >
      {({ describedBy }) =>
        sent ? (
          <>
            <p id={describedBy} className="callout" role="status">
              {sent}
            </p>
            <div className="tm-dialog-actions">
              <Button variant="primary" className="tm-feedback-done" onClick={onClose}>
                Done
              </Button>
            </div>
          </>
        ) : (
          <form className="tm-feedback-form" onSubmit={submit} onPaste={onPaste} noValidate>
            <p id={describedBy} className="callout tm-muted">
              Tell us what's broken, or what you wish Tripmo did. We read every one.
            </p>
            <SegmentedControl
              label="What it is"
              options={KINDS}
              value={kind}
              onChange={(v) => setKind(v as FeedbackKind)}
            />
            <label className="sv-field" htmlFor={textId}>
              <span className="sv-field-label">{asked.label}</span>
              <textarea
                ref={growText}
                id={textId}
                className="sv-field-input tm-feedback-text"
                value={text}
                maxLength={MAX_CHARS}
                rows={5}
                placeholder={asked.placeholder}
                onChange={(e) => setText(e.target.value)}
              />
              {left <= 500 && (
                <span className="sv-field-hint" aria-live="polite">
                  {left === 0 ? "That's the most it takes" : `${left.toLocaleString("en-US")} characters left`}
                </span>
              )}
            </label>
            <div className="tm-feedback-shot">
              {shot ? (
                <figure className="tm-feedback-thumb">
                  {drawn ? (
                    <img src={shot.url} alt="Your screenshot" onError={() => setDrawn(false)} />
                  ) : (
                    // An image this browser can't draw (a HEIC outside Safari) still goes; it's shown as an image.
                    <span className="tm-feedback-thumb-none" role="img" aria-label="Your screenshot">
                      <Icon name="photos" size={24} />
                    </span>
                  )}
                  <figcaption className="caption tm-muted">
                    <span className="tm-feedback-shot-name">{shot.file.name || "Screenshot"}</span>
                    <span>{size(shot.file.size)}</span>
                  </figcaption>
                  <Button variant="quiet" size="sm" disabled={busy} onClick={() => setShot(null)}>
                    Remove
                  </Button>
                </figure>
              ) : (
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon="plus"
                    className="tm-edge-start"
                    disabled={busy}
                    onClick={() => input.current?.click()}
                  >
                    Add a screenshot
                  </Button>
                  <span className="caption tm-muted">
                    An image, up to 10 MB{device() === "computer" ? ", or paste one here" : ""}.
                  </span>
                </>
              )}
              <input
                ref={input}
                type="file"
                accept="image/*,.heic,.heif"
                hidden
                aria-label="Choose a screenshot"
                onChange={(e) => {
                  const picked = e.target.files?.[0];
                  e.target.value = "";
                  void take(picked);
                }}
              />
            </div>
            <label className="tm-feedback-check">
              <input type="checkbox" checked={include} onChange={(e) => setInclude(e.target.checked)} />
              <span className="tm-feedback-check-text">
                <span className="callout">Include what helps us find it</span>
                <span className="caption tm-muted">
                  This page's address, Tripmo's version, your browser and device, the time
                  {ask.requestId ? (
                    <>
                      , {ask.crash ? "" : "and "}the reference of the error you met (
                      <span className="tm-feedback-ref">{ask.requestId}</span>) and any others here
                    </>
                  ) : (
                    `, ${ask.crash ? "" : "and "}the references of any errors here`
                  )}
                  {ask.crash
                    ? ", and the error Tripmo ran into, with where in its code it happened. Never what's in your trips, though that error can rarely quote a word from one."
                    : ". Never what's in your trips."}
                </span>
              </span>
            </label>
            {error && (
              <p className="callout tm-error" role="alert">
                {error}
              </p>
            )}
            <div className="tm-dialog-actions">
              <Button variant="secondary" disabled={busy} onClick={onClose}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={busy}>
                {busy ? "Sending…" : "Send"}
              </Button>
            </div>
          </form>
        )
      }
    </Modal>
  );
}
